<template>
  <ion-page>
    <ion-content class="modern-content">
      <SiteTitle :icon="'grid-outline'" :title="`Table: ${$route.params.name}`" />

      <div class="page-container">
        <PageHeader icon="server-outline" :title="$route.params.name">
          <template #actions>
            <ActionButton variant="secondary" icon="refresh-outline" @click="refreshData">Refresh</ActionButton>
            <ActionButton variant="secondary" icon="download-outline" @click="exportData">Export</ActionButton>
            <ActionButton variant="primary" icon="add-outline" @click="addNewRow">Add Row</ActionButton>
          </template>
        </PageHeader>

        <div class="stats-grid">
          <StatCard icon="list-outline" color="primary" :value="data.length" label="Total Rows" />
          <StatCard icon="grid-outline" color="info" :value="labels.length" label="Columns" />
          <StatCard icon="search-outline" color="success" :value="filteredData.length" label="Filtered Rows" />
        </div>

        <DataCard title="Table Data" :subtitle="`${filteredData.length} row${filteredData.length !== 1 ? 's' : ''}`"
          noPadding>
          <template #actions>
            <SearchBox v-model="searchTerm" placeholder="Search table data..." />
          </template>

          <div class="table-wrapper">
            <LoadingState v-if="loading" message="Loading table data..." />

            <EmptyState v-else-if="data.length === 0" icon="grid-outline" title="No Data Found"
              description="This table is empty or no data matches your search.">
              <ActionButton variant="primary" icon="add-outline" @click="addNewRow">Add First Row</ActionButton>
            </EmptyState>

            <div v-else class="modern-table">
              <div class="table-header">
                <div v-for="(label, index) in labels" :key="label" class="header-cell" :style="colStyle(index)"
                  @click="sortBy(index)">
                  <span class="header-text">{{ label }}</span>
                  <div class="sort-indicator">
                    <ion-icon v-if="sortColumn === index && sortDirection === 'asc'" name="chevron-up-outline"
                      class="sort-active"></ion-icon>
                    <ion-icon v-else-if="sortColumn === index && sortDirection === 'desc'" name="chevron-down-outline"
                      class="sort-active"></ion-icon>
                    <ion-icon v-else name="swap-vertical-outline" class="sort-default"></ion-icon>
                  </div>
                  <div class="resize-handle" @pointerdown.stop="startResize($event, index)" @click.stop></div>
                </div>
                <div class="header-cell actions-header">Actions</div>
              </div>

              <div class="table-body">
                <div v-for="(row, rowIndex) in sortedData" :key="rowIndex" class="table-row">
                  <div v-for="(cell, colIndex) in row" :key="colIndex" class="table-cell" :style="colStyle(colIndex)"
                    @click="editCell(rowIndex, colIndex)">
                    <div v-if="editingCell?.row === rowIndex && editingCell?.col === colIndex" class="cell-edit">
                      <input v-model="editValue" @blur="saveEdit" @keyup.enter="saveEdit" @keyup.escape="cancelEdit"
                        class="cell-input" autofocus />
                    </div>
                    <div v-else class="cell-content">
                      <span v-if="searchTerm && String(cell).toLowerCase().includes(searchTerm.toLowerCase())"
                        v-html="highlightMatch(formatCell(cell), searchTerm)"></span>
                      <span v-else>{{ formatCell(cell) }}</span>
                    </div>
                  </div>
                  <div class="table-cell actions-cell">
                    <div class="action-buttons">
                      <button class="icon-btn edit-btn" @click="editRow(rowIndex)" title="Edit Row">
                        <ion-icon name="create-outline"></ion-icon>
                      </button>
                      <button class="icon-btn delete-btn" @click="deleteRow(rowIndex)" title="Delete Row">
                        <ion-icon name="trash-outline"></ion-icon>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DataCard>

        <DataCard title="Table Schema" :subtitle="`${labels.length} column${labels.length !== 1 ? 's' : ''}`" noPadding>
          <div class="schema-grid">
            <div v-for="(label, index) in labels" :key="label" class="schema-item">
              <div class="schema-icon">
                <ion-icon name="key-outline" v-if="index === 0"></ion-icon>
                <ion-icon name="text-outline" v-else></ion-icon>
              </div>
              <div class="schema-info">
                <h4>{{ label }}</h4>
                <p>{{ index === 0 ? 'Primary Key' : 'Data Column' }}</p>
              </div>
            </div>
          </div>
        </DataCard>
      </div>

      <AppModal v-model="addModalOpen" title="Add Row">
        <div v-if="addFormFields.length > 0" class="add-form">
          <div v-for="field in addFormFields" :key="field.name" class="form-group">
            <label class="form-label">{{ field.name }}</label>
            <input v-if="field.kind === 'number'" v-model="addFormValues[field.name]" type="number"
              :placeholder="field.placeholder" class="form-input" />
            <input v-else-if="field.kind === 'date'" v-model="addFormValues[field.name]" type="date"
              class="form-input" />
            <textarea v-else-if="field.kind === 'textarea'" v-model="addFormValues[field.name]"
              :placeholder="field.placeholder" class="form-textarea" rows="3"></textarea>
            <input v-else v-model="addFormValues[field.name]" type="text" :placeholder="field.placeholder"
              class="form-input" />
          </div>
        </div>
        <LoadingState v-else message="Loading columns..." />
        <template #footer>
          <ActionButton variant="secondary" @click="addModalOpen = false">
            Cancel
          </ActionButton>
          <ActionButton variant="primary" @click="saveNewRow()">
            Add Row
          </ActionButton>
        </template>
      </AppModal>

      <div v-if="successMessage" class="success-toast" :class="{ 'error-toast': messageIsError }">
        <ion-icon :name="messageIsError ? 'alert-circle-outline' : 'checkmark-circle-outline'"></ion-icon>
        {{ successMessage }}
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { defineComponent, ref, getCurrentInstance, computed, onUnmounted } from "vue";
import SiteTitle from "@/components/SiteTitle.vue";
import StatCard from "@/components/StatCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import ActionButton from "@/components/ActionButton.vue";
import AppModal from "@/components/AppModal.vue";
import DataCard from "@/components/DataCard.vue";
import SearchBox from "@/components/SearchBox.vue";
import LoadingState from "@/components/LoadingState.vue";
import EmptyState from "@/components/EmptyState.vue";
import { useRoute } from "vue-router";

export default defineComponent({
  name: "TableDetailView",
  components: {
    SiteTitle,
    StatCard,
    PageHeader,
    ActionButton,
    AppModal,
    DataCard,
    SearchBox,
    LoadingState,
    EmptyState,
  },
  setup() {
    const labels = ref([]);
    const data = ref([]);
    const loading = ref(true);
    const searchTerm = ref('');
    const sortColumn = ref(null);
    const sortDirection = ref('asc');
    const editingCell = ref(null);
    const editValue = ref('');
    const successMessage = ref('');
    const messageIsError = ref(false);
    const primaryKey = ref('id');
    const columnTypes = ref({});
    const addModalOpen = ref(false);
    const addFormValues = ref({});
    const colWidths = ref([]);
    let suppressSortUntil = 0;
    let stopResize = null;

    const route = useRoute();
    const { appContext } = getCurrentInstance();
    const axios = appContext.config.globalProperties.$axios;
    const qs = appContext.config.globalProperties.$qs;

    const loadData = async () => {
      loading.value = true;
      try {
        const response = await axios.post(
          "mysql.php",
          qs.stringify({ getTableByName: route.params.name }),
          {
            headers: {
              "Authorization": localStorage.getItem("token")
            }
          }
        );
        const newLabels = response.data.labels || [];
        if (JSON.stringify(newLabels) !== JSON.stringify(labels.value)) {
          colWidths.value = [];
        }
        labels.value = newLabels;
        data.value = response.data.data;
        primaryKey.value = response.data.primary_key || 'id';
        columnTypes.value = response.data.column_types || {};
      } catch (error) {
        console.error('Error loading table data:', error);
      } finally {
        loading.value = false;
      }
    };

    loadData();

    const filteredData = computed(() => {
      if (!searchTerm.value.trim()) return data.value;

      const searchLower = searchTerm.value.toLowerCase();
      return data.value.filter(row =>
        row.some(cell =>
          String(cell).toLowerCase().includes(searchLower)
        )
      );
    });

    const sortedData = computed(() => {
      if (sortColumn.value === null) return filteredData.value;

      const sorted = [...filteredData.value].sort((a, b) => {
        const aVal = a[sortColumn.value];
        const bVal = b[sortColumn.value];

        // Check if values are numbers
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection.value === 'asc' ? aNum - bNum : bNum - aNum;
        } else {
          const aStr = String(aVal).toLowerCase();
          const bStr = String(bVal).toLowerCase();

          if (sortDirection.value === 'asc') {
            return aStr.localeCompare(bStr);
          } else {
            return bStr.localeCompare(aStr);
          }
        }
      });

      return sorted;
    });

    const updateField = async (rowIndex, fieldName, newValue) => {
      try {
        // Resolve the row from the visible (sorted/filtered) data and
        // address it by primary key, so sorting or searching can't
        // shift the update onto the wrong row.
        const response = await axios.post(
          "mysql.php",
          qs.stringify({
            updateField: true,
            tableName: route.params.name,
            fieldName,
            newValue,
            keyValue: rowKeyValue(rowIndex),
          }),
          {
            headers: {
              "Authorization": localStorage.getItem("token")
            }
          }
        );

        if (response.data && response.data.error) {
          showMessage(response.data.error, true);
          return;
        }

        showMessage('Field updated successfully', false);
        await loadData(); // Reload data after update
      } catch (error) {
        console.error('Error updating field:', error);
        showMessage('Error updating field', true);
      }
    };

    const sortBy = (columnIndex) => {
      // Ignore the click that ends a column resize drag.
      if (Date.now() < suppressSortUntil) {
        return;
      }
      if (sortColumn.value === columnIndex) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortColumn.value = columnIndex;
        sortDirection.value = 'asc';
      }
    };

    const colStyle = (index) => {
      const w = colWidths.value[index];
      if (!w) {
        return {};
      }
      return { width: w + 'px', minWidth: w + 'px', maxWidth: w + 'px', flex: 'none' };
    };

    const startResize = (e, colIndex) => {
      e.preventDefault();
      const headerEl = e.target.closest('.header-cell');
      const startX = e.clientX;
      const startWidth = headerEl ? headerEl.getBoundingClientRect().width : 120;
      let moved = false;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      const onMove = (ev) => {
        moved = true;
        colWidths.value[colIndex] = Math.max(60, Math.round(startWidth + ev.clientX - startX));
      };
      const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        stopResize = null;
        if (moved) {
          suppressSortUntil = Date.now() + 250;
        }
      };
      stopResize = onUp;
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    };

    onUnmounted(() => {
      if (stopResize) {
        stopResize();
      }
    });

    const editCell = (rowIndex, colIndex) => {
      editingCell.value = { row: rowIndex, col: colIndex };
      editValue.value = sortedData.value[rowIndex][colIndex];
    };

    const saveEdit = async () => {
      if (editingCell.value) {
        const { row, col } = editingCell.value;
        const fieldName = labels.value[col];
        await updateField(row, fieldName, editValue.value);
        editingCell.value = null;
        editValue.value = '';
      }
    };

    const cancelEdit = () => {
      editingCell.value = null;
      editValue.value = '';
    };

    const editRow = (rowIndex) => {
      // Implement row editing functionality
      console.log('Edit row:', rowIndex);
    };

    const rowKeyValue = (rowIndex) => {
      const row = sortedData.value[rowIndex];
      let pkIndex = labels.value.indexOf(primaryKey.value);
      if (pkIndex < 0) pkIndex = 0;
      return row ? row[pkIndex] : undefined;
    };

    const deleteRow = async (rowIndex) => {
      if (!confirm('Are you sure you want to delete this row?')) return;

      try {
        const response = await axios.post(
          "mysql.php",
          qs.stringify({
            deleteRow: true,
            tableName: route.params.name,
            keyValue: rowKeyValue(rowIndex),
          }),
          {
            headers: {
              "Authorization": localStorage.getItem("token")
            }
          }
        );

        if (response.data && response.data.error) {
          showMessage(response.data.error, true);
          return;
        }

        showMessage('Row deleted successfully', false);
        await loadData();
      } catch (error) {
        console.error('Error deleting row:', error);
        showMessage('Error deleting row', true);
      }
    };

    const inputKind = (label) => {
      const t = String(columnTypes.value[label] || '').toLowerCase();
      if (/^(tinyint|smallint|mediumint|int|bigint|decimal|numeric|float|double)/.test(t)) return 'number';
      if (/^date$/.test(t)) return 'date';
      if (/^(text|mediumtext|longtext)/.test(t)) return 'textarea';
      return 'text';
    };

    const datetimePlaceholder = (label) => {
      const t = String(columnTypes.value[label] || '').toLowerCase();
      if (/^(datetime|timestamp)/.test(t)) return 'YYYY-MM-DD HH:MM:SS';
      return label;
    };

    const addFormFields = computed(() => {
      return labels.value
        .filter((label) => label !== primaryKey.value)
        .map((label) => ({ name: label, kind: inputKind(label), placeholder: datetimePlaceholder(label) }));
    });

    const addNewRow = () => {
      const values = {};
      addFormFields.value.forEach((field) => {
        values[field.name] = '';
      });
      addFormValues.value = values;
      addModalOpen.value = true;
    };

    const saveNewRow = async () => {
      try {
        const response = await axios.post(
          "mysql.php",
          qs.stringify({
            insertRow: true,
            tableName: route.params.name,
            row: JSON.stringify(addFormValues.value),
          }),
          {
            headers: {
              "Authorization": localStorage.getItem("token")
            }
          }
        );

        if (response.data && response.data.error) {
          showMessage(response.data.error, true);
          return;
        }

        showMessage('Row added successfully', false);
        addModalOpen.value = false;
        addFormValues.value = {};
        await loadData();
      } catch (error) {
        console.error('Error adding row:', error);
        showMessage('Error adding row', true);
      }
    };

    const refreshData = () => {
      loadData();
    };

    const exportData = () => {
      // Implement data export functionality
      console.log('Export data');
    };

    const formatCell = (value) => {
      if (typeof value !== 'string') return value;
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
      if (!m) return value;
      const d = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] || '00'}Z`);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString();
    };

    const highlightMatch = (text, search) => {
      if (!search) return text;
      const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return String(text).replace(regex, '<mark class="highlight">$1</mark>');
    };

    const showMessage = (message, isError) => {
      messageIsError.value = !!isError;
      successMessage.value = message;
      setTimeout(() => {
        successMessage.value = '';
      }, 3000);
    };

    return {
      labels,
      data,
      loading,
      searchTerm,
      sortColumn,
      sortDirection,
      editingCell,
      editValue,
      successMessage,
      messageIsError,
      filteredData,
      sortedData,
      updateField,
      sortBy,
      colStyle,
      startResize,
      addModalOpen,
      addFormFields,
      addFormValues,
      saveNewRow,
      editCell,
      saveEdit,
      cancelEdit,
      editRow,
      deleteRow,
      addNewRow,
      refreshData,
      exportData,
      formatCell,
      highlightMatch,
    };
  },
});
</script>

<style scoped>
.page-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
  background: var(--background);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.table-wrapper {
  overflow-x: auto;
}

.modern-table {
  width: max-content;
  min-width: 100%;
}

.table-header {
  display: flex;
  background: var(--background);
  border-bottom: 2px solid var(--border);
}

.header-cell {
  flex: 1;
  min-width: 120px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  transition: all 0.2s ease;
  position: relative;
}

.header-cell:hover {
  background: var(--border);
}

.resize-handle {
  position: absolute;
  top: 0;
  right: -4px;
  bottom: 0;
  width: 8px;
  cursor: col-resize;
  touch-action: none;
  z-index: 2;
}

.resize-handle:hover::after,
.resize-handle:active::after {
  content: '';
  position: absolute;
  top: 8px;
  bottom: 8px;
  left: 3px;
  width: 2px;
  border-radius: 2px;
  background: var(--primary-color);
}

.actions-header {
  flex: 0 0 140px;
  justify-content: center;
  cursor: default;
}

.actions-header:hover {
  background: var(--background);
}

.header-text {
  font-weight: 600;
}

.sort-indicator {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.sort-indicator ion-icon {
  font-size: 14px;
  transition: all 0.2s ease;
}

.sort-default {
  opacity: 0.3;
}

.sort-active {
  opacity: 1;
  color: var(--primary-color);
}

.header-cell:hover .sort-default {
  opacity: 0.6;
}

/* Table Body */
.table-body {
  background: var(--surface);
}

.table-row {
  display: flex;
  border-bottom: 1px solid var(--border);
  transition: all 0.2s ease;
}

.table-row:hover {
  background: var(--background);
}

.table-row:last-child {
  border-bottom: none;
}

.table-cell {
  flex: 1;
  min-width: 120px;
  padding: 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.table-cell:hover {
  background: rgba(249, 115, 22, 0.05);
}

.actions-cell {
  flex: 0 0 140px;
  justify-content: center;
  padding: 12px 16px;
  cursor: default;
}

.actions-cell:hover {
  background: transparent;
}

.cell-content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
}

.cell-edit {
  width: 100%;
}

.cell-input {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid var(--primary-color);
  border-radius: var(--radius);
  font-size: 14px;
  background: var(--surface);
  color: var(--text-primary);
  outline: none;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 8px;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.edit-btn {
  background: rgba(249, 115, 22, 0.12);
  color: var(--primary-color);
}

.edit-btn:hover {
  background: rgba(249, 115, 22, 0.22);
  transform: scale(1.05);
}

.delete-btn {
  background: rgba(235, 68, 90, 0.12);
  color: var(--danger-color);
}

.delete-btn:hover {
  background: rgba(235, 68, 90, 0.22);
  transform: scale(1.05);
}

/* Schema Grid */
.schema-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 24px;
}

.schema-item {
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.schema-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius);
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(234, 88, 12, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  font-size: 20px;
  flex-shrink: 0;
}

.schema-info h4 {
  margin: 0 0 4px 0;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.schema-info p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 12px;
}

/* Highlight Match */
.highlight {
  background: #ffe082;
  color: #d32f2f;
  padding: 0 2px;
  border-radius: 2px;
  font-weight: 600;
}

/* Success Toast */
.success-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: rgba(5, 150, 105, 0.95);
  color: white;
  padding: 16px 20px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  z-index: 10001;
  backdrop-filter: blur(8px);
  box-shadow: var(--shadow-lg);
  animation: slideInRight 0.3s ease;
}

.error-toast {
  background: rgba(220, 38, 38, 0.95);
}

/* Add Row Form */
.add-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  font-size: 14px;
  background: var(--background);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s ease;
}

.form-input:focus,
.form-textarea:focus {
  border-color: var(--primary-color);
}

.form-textarea {
  resize: vertical;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-container {
    padding: 16px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .header-cell,
  .table-cell {
    min-width: 100px;
    padding: 12px 8px;
    font-size: 12px;
  }

  .cell-content {
    max-width: 80px;
  }

  .schema-grid {
    grid-template-columns: 1fr;
  }
}
</style>
